/**
 * ✅ QUICK SALE ÜRÜN ARAMA HOOK'U
 * 
 * Bu hook, hızlı satış ekranında ürün arama işlemlerini yönetir:
 * - getWarehouseNames: Depo adlarını getir
 * - validateSearchPrerequisites: Arama ön kontrollerini yap
 * - searchAndAddProductToCart: Ürün ara ve sepete ekle
 * - handleSearchSubmit: Ana arama fonksiyonu
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback } from 'react';
import salesService from '../../../../services/salesService';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';
import type { InventoryItem, Warehouse, ScreenSettings, CartItem } from '../types';
import { parseWeightBarcode, DEFAULT_WEIGHT_BARCODE_PREFIX } from '../utils/weightBarcode';
import type { ScanAlertInput } from './useScanAlerts';

// Hook parametreleri için tip tanımı
export interface UseQuickSaleProductSearchParams {
  // Temel bilgiler
  companyId: string | null;
  isRestaurantMode: boolean;
  
  // Ekran ayarları
  screenSettings: ScreenSettings | null;
  currentScreen: { id: string; name: string } | null;
  
  // Yükleme durumları
  loadingScreenSettings: boolean;
  loadingInventory: boolean;
  loadingWarehouses: boolean;
  
  // Depo bilgileri
  allWarehouses: Warehouse[];
  
  // Miktar modu
  isQuantityModeActive: boolean;
  quantityInputValue: string;
  setIsQuantityModeActive: React.Dispatch<React.SetStateAction<boolean>>;
  setQuantityInputValue: React.Dispatch<React.SetStateAction<string>>;
  
  // Sepet işlemleri
  cart: CartItem[];
  addToCart: (product: any, quantity?: number, skipOptionModals?: boolean) => void;
  addToCartWithRestaurant: (product: any, quantity?: number) => void;
  
  // UI işlemleri
  showAlertModal: (title: string, message: string, showQuickAdd?: boolean) => void;
  /**
   * Okutulan barkod sepete eklenemediğinde çağrılır. Engelleyici modal yerine
   * ses çalar ve sepetteki uyarı şeridine kayıt düşer; böylece kasiyer ekrana
   * bakmadan okutmaya devam edebilir ve sonraki ürünler sepete girer.
   */
  pushScanAlert: (alert: ScanAlertInput) => void;
  focusSearchBar: () => void;
  
  // ✅ YENİ: Şube bazlı fiyatlandırma
  branchId?: string | null;

  // ✅ YENİ: Şubenin deposu (barkod aramasında öncelikli depo olarak kullanılır)
  branchWarehouseId?: string | null;
  
  // ✅ YENİ: Kasiyer sistemi kontrol (Normal hızlı satış için)
  useCashierSystem?: boolean;
  currentCashierId?: string | null;
  onOpenCashierLoginWithPendingProduct?: (product: any, quantity: number) => void;

  // ✅ YENİ: Paket fiyat seçimi (grid'deki useQuickSaleProducts ile aynı mantık).
  // isPackageMode veya screenSettings.stockVisibility.usePackagePrices açıksa
  // şube PackagePrice öncelikli, yoksa SalePrice'a fallback.
  isPackageMode?: boolean;
  usePackagePrices?: boolean;

  // ✅ YENİ: Tartı etiketi (ağırlık barkodu) okuma
  /** Ekran ayarındaki "Ağırlık barkodu oku". Kapalıysa barkod normal ürün araması olarak gider. */
  readWeightBarcode?: boolean;
  /** Kabul edilecek prefix'ler; tanımlı tartılardan gelir, yoksa varsayılan 27. */
  weightBarcodePrefixes?: string[];
}

/** searchAndAddProductToCart'ın ağırlık barkodu yolundan çağrıldığı durum. */
interface SearchOverrides {
  /** API araması yapılmadan doğrudan sepete alınacak ürün. */
  preFetchedProduct?: any;
  /** Etiketten çözülen ağırlık; numpad miktarının yerine geçer. */
  forcedQuantity?: number;
  /** Tam 1.000 kg tartılan etikette kg modalının açılmasını engeller. */
  skipOptionModals?: boolean;
}

// Hook dönüş tipi
export interface UseQuickSaleProductSearchReturn {
  getWarehouseNames: (warehouseIds: string[]) => string;
  validateSearchPrerequisites: (configuredWarehouseIds: string[] | undefined) => { 
    isValid: boolean; 
    errorTitle?: string; 
    errorMessage?: string; 
  };
  searchAndAddProductToCart: (searchTermLower: string, configuredWarehouseIds: string[], overrides?: SearchOverrides) => Promise<boolean>;
  handleSearchSubmit: (searchTermParam: string) => Promise<void>;
  /** Autocomplete dropdown'dan seçilen ürünü doğrulayıp sepete ekler (yeniden API çağrısı yapmadan) */
  handleAutocompleteSelect: (rawProduct: any) => Promise<void>;
}

/**
 * Hızlı satış için ürün arama hook'u
 */
export const useQuickSaleProductSearch = ({
  companyId,
  isRestaurantMode,
  screenSettings,
  currentScreen,
  loadingScreenSettings,
  loadingInventory,
  loadingWarehouses,
  allWarehouses,
  isQuantityModeActive,
  quantityInputValue,
  setIsQuantityModeActive,
  setQuantityInputValue,
  cart,
  addToCart,
  addToCartWithRestaurant,
  showAlertModal,
  pushScanAlert,
  focusSearchBar,
  branchId, // ✅ YENİ: Şube bazlı fiyatlandırma
  branchWarehouseId, // ✅ YENİ: Şubenin deposu
  // ✅ YENİ: Kasiyer sistemi kontrol
  useCashierSystem,
  currentCashierId,
  onOpenCashierLoginWithPendingProduct,
  // ✅ YENİ: Paket fiyat toggle
  isPackageMode = false,
  usePackagePrices = false,
  // ✅ YENİ: Tartı etiketi okuma
  readWeightBarcode = false,
  weightBarcodePrefixes,
}: UseQuickSaleProductSearchParams): UseQuickSaleProductSearchReturn => {
  // ✅ YENİ: Paket modu YA DA ekran ayarı açıksa paket fiyatı öncelikli
  const shouldUsePackagePrice = isPackageMode || usePackagePrices;

  /**
   * Depo ID'lerinden depo adlarını string olarak döndür
   */
  const getWarehouseNames = useCallback((warehouseIds: string[]): string => {
    if (!warehouseIds || warehouseIds.length === 0) return 'Hiçbiri';
    return warehouseIds
      .map(id => allWarehouses.find(w => w.id === id)?.name || id)
      .join(', ') || 'Bilinmeyen Depolar';
  }, [allWarehouses]);

  /**
   * Arama için ön kontrolleri yap
   */
  const validateSearchPrerequisites = useCallback((
    configuredWarehouseIds: string[] | undefined
  ): { isValid: boolean; errorTitle?: string; errorMessage?: string } => {
    console.log('┌─────────────────────────────────────────────────────────┐');
    console.log('│ 🔍 VALİDASYON KONTROLLERI (validateSearchPrerequisites)│');
    console.log('└─────────────────────────────────────────────────────────┘');
    
    // Yükleme kontrolü
    console.log('⏳ [KONTROL 1/3] Yükleme durumu kontrol ediliyor...');
    console.log('📊 [YÜKLEME DURUMU]:', {
      loadingScreenSettings: loadingScreenSettings,
      loadingInventory: loadingInventory,
      loadingWarehouses: loadingWarehouses
    });
    
    if (loadingScreenSettings || loadingInventory || loadingWarehouses) {
      console.error('❌ [VALİDASYON HATASI 1/3] Veriler hala yükleniyor!');
      return {
        isValid: false,
        errorTitle: "Lütfen Bekleyin",
        errorMessage: "Ayarlar, ürünler veya depolar henüz yükleniyor..."
      };
    }
    console.log('✅ [KONTROL 1/3] Tüm veriler yüklendi');

    // Ekran ayarları kontrolü
    console.log('⚙️ [KONTROL 2/3] Ekran ayarları kontrol ediliyor...');
    console.log('📋 [EKRAN AYARLARI]:', {
      screenSettings: !!screenSettings,
      screenId: currentScreen?.id,
      screenName: currentScreen?.name,
      stockVisibility: !!screenSettings?.stockVisibility
    });
    
    if (!screenSettings) {
      console.error('❌ [VALİDASYON HATASI 2/3] Ekran ayarları yok!');
      return {
        isValid: false,
        errorTitle: "Hata",
        errorMessage: `Ekran ayarları yüklenemedi veya bulunamadı. Bu nedenle depo bilgisi alınamadı.\nLütfen sayfayı yenileyin veya yöneticinize danışın.`
      };
    }
    console.log('✅ [KONTROL 2/3] Ekran ayarları mevcut');

    // Depo kontrolü
    console.log('📦 [KONTROL 3/3] Depo ayarları kontrol ediliyor...');
    console.log('🏭 [DEPO BİLGİLERİ]:', {
      configuredWarehouseIds: configuredWarehouseIds,
      depoSayisi: configuredWarehouseIds?.length || 0,
      depoAdlari: configuredWarehouseIds?.map(wid => {
        const warehouse = allWarehouses.find(w => w.id === wid);
        return { id: wid, name: warehouse?.name || 'Bilinmeyen' };
      }) || []
    });
    
    if (!configuredWarehouseIds || configuredWarehouseIds.length === 0) {
      console.error('❌ [VALİDASYON HATASI 3/3] Hiç depo seçilmemiş!');
      return {
        isValid: false,
        errorTitle: "Depo Seçilmemiş",
        errorMessage: `Bu ekran için Ayarlar bölümünden en az bir depo seçmeniz gerekiyor. Şu anda ayarlı depo bulunmuyor.\nAyarlı Depolar: Yok`
      };
    }
    console.log('✅ [KONTROL 3/3] Depo ayarları mevcut:', configuredWarehouseIds.length, 'adet');

    console.log('✅✅✅ [VALİDASYON BAŞARILI] Tüm kontroller geçildi!');
    return { isValid: true };
  }, [loadingScreenSettings, loadingInventory, loadingWarehouses, screenSettings, currentScreen, allWarehouses]);

  /**
   * Ürün ara ve sepete ekle (SQL'den direkt arama)
   */
  const searchAndAddProductToCart = useCallback(async (
    searchTermLower: string,
    configuredWarehouseIds: string[],
    overrides?: SearchOverrides
  ): Promise<boolean> => {
    console.log('┌─────────────────────────────────────────────────────────┐');
    console.log('│ 🔍 SQL ARAMA VE SEPETE EKLEME İŞLEMİ                   │');
    console.log('└─────────────────────────────────────────────────────────┘');
    console.log('📥 [API ÖNCESİ] Gönderilecek parametreler:', {
      companyId: companyId,
      arananTerim: searchTermLower,
      depoIdleri: configuredWarehouseIds,
      depoSayısı: configuredWarehouseIds.length
    });

    try {
      // Ağırlık barkodu yolunda ürün zaten tartı koduyla bulundu; arama atlanır
      // ve buradan sonraki fiyat/stok/şube fiyatı/kasiyer kontrolleri aynen işler
      const searchResults: any[] = overrides?.preFetchedProduct
        ? [overrides.preFetchedProduct]
        : await (async () => {
            console.log('⏳ [API ÇAĞRISI] salesService.searchProducts() çağrılıyor...');
            return salesService.searchProducts(searchTermLower, configuredWarehouseIds);
          })();

      console.log('📦 [API YANITI] SQL sonuçları:', {
        bulunanÜrünSayısı: searchResults.length,
        örnek3Ürün: searchResults.slice(0, 3).map((p: any) => ({
          id: p.id || p.Id,
          name: p.name || p.Name,
          barcode: p.barcode || p.Barcode,
          warehouseId: p.warehouseId || p.WarehouseId,
          quantity: p.quantity || p.Quantity || 0,
          totalQuantity: p.totalQuantity || p.TotalQuantity || 0,
          salePrice: p.salePrice || p.SalePrice,
          canBeNegative: p.canBeNegative || p.CanBeNegative
        }))
      });
      
      // TÜM SONUÇLARI DETAYLI LOGLA
      if (searchResults.length > 0) {
        console.log('📋 [API YANITI] TÜM BULUNAN ÜRÜNLER:');
        searchResults.forEach((p: any, index: number) => {
          console.log(`  [${index + 1}/${searchResults.length}]`, {
            id: p.id || p.Id,
            name: p.name || p.Name,
            barcode: p.barcode || p.Barcode,
            warehouseId: p.warehouseId || p.WarehouseId,
            warehouseName: allWarehouses.find(w => w.id === (p.warehouseId || p.WarehouseId))?.name || 'Bilinmeyen',
            quantity: p.quantity || p.Quantity || 0,
            totalQuantity: p.totalQuantity || p.TotalQuantity || 0,
            salePrice: p.salePrice || p.SalePrice,
            canBeNegative: p.canBeNegative || p.CanBeNegative
          });
        });
      }

      // Ürün bulunamadı
      if (!searchResults || searchResults.length === 0) {
        const warehouseNames = getWarehouseNames(configuredWarehouseIds);
        console.error('┌─────────────────────────────────────────────────────────┐');
        console.error('│ ❌ ÜRÜN BULUNAMADI                                      │');
        console.error('└─────────────────────────────────────────────────────────┘');
        console.error('🔴 [HATA] SQL\'den hiçbir ürün gelmedi:', {
          arananTerim: searchTermLower,
          arananDepolar: configuredWarehouseIds,
          depoAdları: warehouseNames
        });
        pushScanAlert({
          term: searchTermLower,
          title: "Bulunamadı",
          message: `'${searchTermLower}' isimli/barkodlu ürün, seçili depolarda bulunamadı.\nAranan Depolar: ${warehouseNames}`,
          canQuickAdd: true,
        });
        focusSearchBar();
        return false;
      }

      console.log('🔍 [ÜRÜN SEÇİMİ] Tam eşleşme aranıyor...');
      // İlk bulunan ürünü al (tam eşleşme varsa onu, yoksa ilk sonucu)
      const exactMatch = searchResults.find((item: any) => {
        const itemName = (item.name || item.Name || '').toLowerCase();
        const itemBarcode = (item.barcode || item.Barcode || '').toLowerCase();
        const eslestiMi = itemName === searchTermLower || itemBarcode === searchTermLower;
        
        if (eslestiMi) {
          console.log('✅ [TAM EŞLEŞME] Bulundu:', {
            id: item.id || item.Id,
            name: item.name || item.Name,
            barcode: item.barcode || item.Barcode,
            eslesmeNedeni: itemName === searchTermLower ? 'İsim' : 'Barkod'
          });
        }
        
        return eslestiMi;
      });

      const foundProduct = exactMatch || searchResults[0];
      
      console.log('🎯 [ÜRÜN SEÇİLDİ]:', {
        seçimTipi: exactMatch ? 'TAM EŞLEŞME' : 'İLK SONUÇ',
        id: foundProduct.id || foundProduct.Id,
        name: foundProduct.name || foundProduct.Name,
        barcode: foundProduct.barcode || foundProduct.Barcode
      });

      console.log('🔄 [VERİ DÖNÜŞÜMÜ] API verisinden InventoryItem formatına çeviriliyor...');
      console.log('📥 [HAM VERİ] foundProduct:', foundProduct);
      
      // Depo bilgisini warehouseStocks'tan al
      const warehouseStocks = foundProduct.warehouseStocks || foundProduct.WarehouseStocks || [];
      let effectiveWarehouseId = foundProduct.warehouseId?.toString() || foundProduct.WarehouseId?.toString() || '';
      let effectiveTotalQuantity = foundProduct.totalQuantity || foundProduct.TotalQuantity || 0;
      
      console.log('📦 [DEPO STOK BİLGİSİ]:', {
        warehouseStocksLength: warehouseStocks.length,
        warehouseStocks: warehouseStocks
      });
      
      // Eğer warehouseId boşsa ve warehouseStocks varsa, yapılandırılmış depolardan birini seç
      if (!effectiveWarehouseId && warehouseStocks.length > 0) {
        // Öncelik: Yapılandırılmış depolardan ilkini seç
        const configuredStock = warehouseStocks.find((ws: any) => 
          configuredWarehouseIds.includes(ws.warehouseId?.toString() || ws.WarehouseId?.toString() || '')
        );
        
        if (configuredStock) {
          effectiveWarehouseId = configuredStock.warehouseId?.toString() || configuredStock.WarehouseId?.toString() || '';
          effectiveTotalQuantity = configuredStock.quantity || configuredStock.Quantity || 0;
          console.log('✅ [DEPO SEÇİMİ] Yapılandırılmış depo bulundu:', {
            warehouseId: effectiveWarehouseId,
            warehouseName: configuredStock.warehouseName || configuredStock.WarehouseName,
            quantity: effectiveTotalQuantity
          });
        } else {
          // Yapılandırılmış depoda bulunamadı, ilk depoyu al
          const firstStock = warehouseStocks[0];
          effectiveWarehouseId = firstStock.warehouseId?.toString() || firstStock.WarehouseId?.toString() || '';
          effectiveTotalQuantity = firstStock.quantity || firstStock.Quantity || 0;
          console.log('⚠️ [DEPO SEÇİMİ] Yapılandırılmış depoda yok, ilk depo seçildi:', {
            warehouseId: effectiveWarehouseId,
            warehouseName: firstStock.warehouseName || firstStock.WarehouseName,
            quantity: effectiveTotalQuantity
          });
        }
      }
      
      // Ürünü InventoryItem formatına çevir
      const inventoryItem: InventoryItem = {
        id: foundProduct.id?.toString() || foundProduct.Id?.toString() || '',
        name: foundProduct.name || foundProduct.Name || '',
        salePrice: foundProduct.salePrice || foundProduct.SalePrice || 0,
        categoryId: foundProduct.categoryId?.toString() || foundProduct.CategoryId?.toString() || '',
        warehouseId: effectiveWarehouseId,
        quantity: effectiveTotalQuantity,
        unit: foundProduct.unit || foundProduct.Unit || 'adet',
        barcode: foundProduct.barcode || foundProduct.Barcode || '',
        imageUrl: foundProduct.thumbnailUrl || foundProduct.ThumbnailUrl || foundProduct.imageUrl || foundProduct.ImageUrl || null,
        vatRate: foundProduct.vatRate || foundProduct.VatRate || 0,
        vatStatus: foundProduct.vatStatus || foundProduct.VatStatus || 'excluded',
        canBeNegative: foundProduct.canBeNegative || foundProduct.CanBeNegative || false,
        departmentNo: foundProduct.departmentNo || foundProduct.DepartmentNo || null,
        stockCode: foundProduct.stockCode || foundProduct.StockCode || '',
      };
      
      console.log('🧾 [KDV DEBUG - SEARCH] VatStatus:', {
        'foundProduct.vatStatus': foundProduct.vatStatus,
        'foundProduct.VatStatus': foundProduct.VatStatus,
        'inventoryItem.vatStatus': inventoryItem.vatStatus
      });
      
      // ✅ YENİ: ŞUBE BAZLI FİYATLANDIRMA KONTROLÜ
      const useBranchPricing = foundProduct.useBranchPricing ?? foundProduct.UseBranchPricing ?? false;
      console.log('🏬 [ŞUBE FİYAT] useBranchPricing kontrolü:', { 
        useBranchPricing, 
        branchId,
        'foundProduct.useBranchPricing': foundProduct.useBranchPricing,
        'foundProduct.UseBranchPricing': foundProduct.UseBranchPricing,
        'tümFoundProductKeys': Object.keys(foundProduct).filter(k => k.toLowerCase().includes('branch'))
      });
      
      if (useBranchPricing && branchId && companyId) {
        try {
          console.log(`🔍 [ŞUBE FİYAT] "${inventoryItem.name}" için şube fiyatı aranıyor...`);
          const branchPricesDict = await InventoryService.getBulkBranchPrices(companyId, branchId, [inventoryItem.id]);
          
          // Dictionary'den ürün ID'sine göre fiyatı al
          const itemIdLower = inventoryItem.id.toLowerCase();
          const branchPrice = branchPricesDict[itemIdLower] || branchPricesDict[inventoryItem.id];
          
          if (branchPrice) {
            const branchSalePrice = branchPrice.salePrice || 0;
            // ✅ YENİ: Paket modu veya ekran ayarı açıksa PackagePrice öncelik, yoksa SalePrice
            const branchPackagePrice = (branchPrice as any).packagePrice || (branchPrice as any).PackagePrice || 0;
            const effectivePrice = (shouldUsePackagePrice && branchPackagePrice > 0)
              ? branchPackagePrice
              : branchSalePrice;

            console.log(`💰 [ŞUBE FİYAT] "${inventoryItem.name}" için şube fiyatı bulundu:`, {
              eskiFiyat: inventoryItem.salePrice,
              yeniFiyat: effectivePrice,
              branchSalePrice,
              branchPackagePrice,
              shouldUsePackagePrice,
              isPackageMode,
              usePackagePrices,
              branchId: branchId
            });

            // Şube fiyatını uygula (KDV dahil fiyat)
            inventoryItem.salePrice = effectivePrice;
            // ⚠️ TERMİNOLOJİ TERSİNE ÇEVRİLDİ! Şube fiyatları KDV dahil olduğu için 'included' kullanılıyor
            // 'included' = KDV dahil fiyat = calculateTotal'da KDV eklenmeyecek
            inventoryItem.vatStatus = 'included';
          } else {
            console.log(`⚠️ [ŞUBE FİYAT] "${inventoryItem.name}" için şube fiyatı bulunamadı, varsayılan fiyat kullanılacak.`);
          }
        } catch (error) {
          console.error(`❌ [ŞUBE FİYAT] Şube fiyatı alınırken hata:`, error);
          // Hata durumunda varsayılan fiyatla devam et
        }
      }

      console.log('┌─────────────────────────────────────────────────────────┐');
      console.log('│ 📦 ÜRÜN BİLGİLERİ (InventoryItem Format)               │');
      console.log('└─────────────────────────────────────────────────────────┘');
      console.log('🎯 [ÜRÜN DETAYLARI]:', {
        id: inventoryItem.id,
        name: inventoryItem.name,
        barcode: inventoryItem.barcode,
        stockCode: inventoryItem.stockCode,
        warehouseId: inventoryItem.warehouseId,
        warehouseName: allWarehouses.find(w => w.id === inventoryItem.warehouseId)?.name || 'Bilinmeyen',
        '⭐ STOK MİKTARI': inventoryItem.quantity,
        '💰 SATIŞ FİYATI': inventoryItem.salePrice,
        '🔄 EKSİYE DÜŞEBİLİR Mİ': inventoryItem.canBeNegative,
        unit: inventoryItem.unit,
        categoryId: inventoryItem.categoryId,
        vatRate: inventoryItem.vatRate,
        vatStatus: inventoryItem.vatStatus
      });

      // FİYAT KONTROLÜ
      console.log('💰 [KONTROL 1/2] Fiyat kontrolü yapılıyor...');
      if (!inventoryItem.salePrice || inventoryItem.salePrice === 0) {
        const warehouseNames = getWarehouseNames(configuredWarehouseIds);
        console.error('┌─────────────────────────────────────────────────────────┐');
        console.error('│ ❌ FİYAT TANIMSIZ HATASI                               │');
        console.error('└─────────────────────────────────────────────────────────┘');
        console.error('🔴 [HATA] Ürün fiyatı 0 veya tanımsız:', {
          productName: inventoryItem.name,
          salePrice: inventoryItem.salePrice,
          arananDepolar: warehouseNames
        });
        pushScanAlert({
          term: searchTermLower,
          title: "Fiyat Tanımsız",
          // Ürün kartı zaten var; yeni kart açmak mükerrer kayıt yaratır
          message: `'${inventoryItem.name}' ürününün satış fiyatı tanımlı değil.\nAranan Depolar: ${warehouseNames}`,
          canQuickAdd: false,
        });
        focusSearchBar();
        return false;
      }
      console.log('✅ [KONTROL 1/2] Fiyat kontrolü BAŞARILI:', inventoryItem.salePrice);

      // STOK KONTROLÜ
      console.log('📦 [KONTROL 2/2] Stok kontrolü yapılıyor...');
      const stokMiktari = inventoryItem.quantity ?? 0;
      console.log('📊 [STOK DETAY]:', {
        stokMiktari: stokMiktari,
        eksiDusebilir: inventoryItem.canBeNegative,
        kontrolGereklimi: stokMiktari <= 0 && !inventoryItem.canBeNegative
      });
      
      if (stokMiktari <= 0 && !inventoryItem.canBeNegative) {
        const warehouseNames = getWarehouseNames(configuredWarehouseIds);
        console.error('┌─────────────────────────────────────────────────────────┐');
        console.error('│ ❌ STOK YOK HATASI                                      │');
        console.error('└─────────────────────────────────────────────────────────┘');
        console.error('🔴 [HATA] Ürün stoğu yetersiz:', {
          productName: inventoryItem.name,
          mevcutStok: stokMiktari,
          eksiDusebilir: inventoryItem.canBeNegative,
          warehouseId: inventoryItem.warehouseId,
          warehouseName: allWarehouses.find(w => w.id === inventoryItem.warehouseId)?.name || 'Bilinmeyen',
          arananDepolar: warehouseNames
        });
        pushScanAlert({
          term: searchTermLower,
          title: "Stok Yok",
          message: `'${inventoryItem.name}' ürününün stoğu yetersiz!\n\nMevcut Stok: ${stokMiktari}\nDepo: ${allWarehouses.find(w => w.id === inventoryItem.warehouseId)?.name || 'Bilinmeyen'}\n\nAranan Depolar: ${warehouseNames}`,
          canQuickAdd: false,
        });
        focusSearchBar();
        return false;
      }
      console.log('✅ [KONTROL 2/2] Stok kontrolü BAŞARILI');
      if (inventoryItem.canBeNegative) {
        console.log('ℹ️ [STOK BİLGİ] Bu ürün eksiye düşebilir, stok kontrolü yapılmadı');
      }

      console.log('┌─────────────────────────────────────────────────────────┐');
      console.log('│ 🛒 SEPETE EKLEME İŞLEMİ                                │');
      console.log('└─────────────────────────────────────────────────────────┘');
      
      // Miktar hesapla — etiketten çözülen ağırlık numpad'in önüne geçer
      const quantityFromNumpad = parseInt(quantityInputValue);
      const quantityToAdd = overrides?.forcedQuantity
        ?? (isQuantityModeActive && !isNaN(quantityFromNumpad) && quantityFromNumpad > 0
          ? quantityFromNumpad
          : 1);

      console.log('🔢 [MİKTAR HESAPLAMA]:', {
        quantityInputValue: quantityInputValue,
        isQuantityModeActive: isQuantityModeActive,
        quantityFromNumpad: quantityFromNumpad,
        quantityToAdd: quantityToAdd
      });

      // Sepete ekle
      const productForCart = {
        id: inventoryItem.id,
        name: inventoryItem.name,
        price: inventoryItem.salePrice,
        salePrice: inventoryItem.salePrice,
        unit: inventoryItem.unit,
        vatRate: inventoryItem.vatRate,
        vatStatus: inventoryItem.vatStatus,
        canBeNegative: inventoryItem.canBeNegative,
        departmentNo: inventoryItem.departmentNo,
        warehouseId: inventoryItem.warehouseId,
        barcode: inventoryItem.barcode,
        stockCode: inventoryItem.stockCode,
        quantity: inventoryItem.quantity
      };
      
      console.log('📦 [SEPETE EKLENECEk ÜRÜN]:', {
        id: productForCart.id,
        name: productForCart.name,
        price: productForCart.price,
        eklenecekMiktar: quantityToAdd,
        mevcutStok: productForCart.quantity,
        canBeNegative: productForCart.canBeNegative,
        warehouseId: productForCart.warehouseId
      });
      
      // Restaurant moduna göre doğru fonksiyonu çağır
      console.log('🍽️ [MOD KONTROLÜ]:', {
        isRestaurantMode: isRestaurantMode,
        kullanilacakFonksiyon: isRestaurantMode ? 'addToCartWithRestaurant' : 'addToCart'
      });
      
      if (isRestaurantMode) {
        console.log('🍽️ [RESTAURANT MODU] addToCartWithRestaurant çağrılıyor...');
        addToCartWithRestaurant(productForCart, quantityToAdd);
      } else {
        // ✅ YENİ: Normal hızlı satışta kasiyer sistemi kontrolü
        if (useCashierSystem && !currentCashierId) {
          console.log('🚫 [KASİYER KONTROL] Kasiyer sistemi aktif ama kasiyer giriş yapmamış!');
          console.log('🔐 [KASİYER KONTROL] Bekleyen ürün kaydedilip kasiyer giriş modalı açılacak...');
          
          // Callback varsa çağır (ürünü beklet ve modal aç)
          if (onOpenCashierLoginWithPendingProduct) {
            onOpenCashierLoginWithPendingProduct(productForCart, quantityToAdd);
          }
          
          // Miktar modunu kapat
          if (isQuantityModeActive) {
            setIsQuantityModeActive(false);
            setQuantityInputValue('');
          }
          
          return false; // Sepete ekleme yapılmadı
        }
        
        console.log('🛒 [NORMAL MOD] addToCart çağrılıyor...');
        addToCart(productForCart, quantityToAdd, overrides?.skipOptionModals);
        
        // Arama sonrası fokusla
        focusSearchBar();
      }
      
      // Miktar modunu kapat
      if (isQuantityModeActive) {
        console.log('🔢 [MİKTAR MODU] Kapatılıyor...');
        setIsQuantityModeActive(false);
        setQuantityInputValue('');
      }

      console.log('✅ [SEPETE EKLEME BAŞARILI]');
      console.log('ℹ️ [NOT] Sepet state async olarak güncelleniyor, yeni miktar bir sonraki render\'da görünecek');
      return true;
    } catch (error: any) {
      console.error('┌─────────────────────────────────────────────────────────┐');
      console.error('│ ❌❌❌ KRİTİK HATA - ARAMA İŞLEMİ BAŞARISIZ ❌❌❌        │');
      console.error('└─────────────────────────────────────────────────────────┘');
      console.error('🔴 [HATA DETAYLARI]:', {
        hataMesaji: error?.message || 'Bilinmeyen hata',
        hataStack: error?.stack,
        hataName: error?.name,
        hataTipi: typeof error,
        tamHata: error,
        apiResponse: error?.response?.data,
        apiStatus: error?.response?.status,
        apiStatusText: error?.response?.statusText,
        arananTerim: searchTermLower,
        companyId: companyId,
        configuredWarehouseIds: configuredWarehouseIds
      });
      showAlertModal(
        "Arama Hatası",
        `Ürün aranırken bir hata oluştu: ${error?.message || error}`
      );
      return false;
    }
  }, [companyId, isRestaurantMode, allWarehouses, isQuantityModeActive, quantityInputValue, setIsQuantityModeActive, setQuantityInputValue, addToCart, addToCartWithRestaurant, showAlertModal, pushScanAlert, focusSearchBar, getWarehouseNames, branchId, isPackageMode, usePackagePrices, shouldUsePackagePrice]);

  /**
   * Ana arama fonksiyonu (orchestrator)
   */
  const handleSearchSubmit = useCallback(async (searchTermParam: string) => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔍 [BARKOD ARAMA BAŞLADI] - DETAYLI LOG');
    console.log('═══════════════════════════════════════════════════════════');
    
    const term = searchTermParam.trim();
    console.log('📝 [1/6] ARAMA TERİMİ KONTROL:', { 
      girilen: searchTermParam, 
      trimmed: term,
      uzunluk: term.length,
      bosMu: !term 
    });
    
    if (!term) {
      console.log('⚠️ [ARAMA İPTAL] Arama terimi boş');
      console.log('═══════════════════════════════════════════════════════════');
      return;
    }

    console.log('📋 [2/6] EKRAN AYARLARI KONTROL:', {
      isRestaurantMode: isRestaurantMode,
      companyId: companyId || 'YOK',
      currentScreenId: currentScreen?.id || 'YOK',
      currentScreenName: currentScreen?.name || 'YOK',
      screenSettings: {
        var: !!screenSettings,
        stockVisibility: !!screenSettings?.stockVisibility,
        stockVisibilityWarehouses: screenSettings?.stockVisibility?.warehouses || []
      }
    });

    // 1. Depo ayarlarını al
    // Şube deposu varsa onu listeye ekle (öncelikli); yoksa sadece stockVisibility listesi kullanılır
    const baseWarehouseIds = screenSettings?.stockVisibility?.warehouses ?? [];
    const configuredWarehouseIds: string[] = branchWarehouseId
      ? [branchWarehouseId, ...baseWarehouseIds.filter((id: string) => id !== branchWarehouseId)]
      : baseWarehouseIds;
    
    console.log('📦 [3/6] DEPO AYARLARI:', {
      yapılandırılmışDepolar: configuredWarehouseIds,
      depoSayısı: configuredWarehouseIds?.length || 0,
      depoAdları: configuredWarehouseIds?.map(wid => {
        const warehouse = allWarehouses.find(w => w.id === wid);
        return { id: wid, name: warehouse?.name || 'Bilinmeyen' };
      }) || []
    });

    // 2. Ön kontrolleri yap
    const validation = validateSearchPrerequisites(configuredWarehouseIds);
    console.log('✅ [4/6] VALİDASYON SONUCU:', {
      basarili: validation.isValid,
      hataBaslik: validation.errorTitle || 'YOK',
      hataMesaj: validation.errorMessage || 'YOK'
    });
    
    if (!validation.isValid) {
      console.error('❌ [ARAMA DURDURULDU] Validasyon hatası:', validation);
      console.log('═══════════════════════════════════════════════════════════');
      showAlertModal(validation.errorTitle!, validation.errorMessage!);
      return;
    }

    // 2.5. Tartı etiketi mi? Ağırlık barkodu ise ürün, kodun ortasındaki 5 hane
    // ile bulunur ve miktar barkoddan gelen ağırlık olur. Etiket değilse bu dal
    // sessizce atlanır ve barkod normal ürün araması olarak devam eder.
    if (readWeightBarcode) {
      const weightLabel = parseWeightBarcode(
        term,
        weightBarcodePrefixes?.length ? weightBarcodePrefixes : [DEFAULT_WEIGHT_BARCODE_PREFIX]
      );

      if (weightLabel) {
        console.log('⚖️ [TARTI ETİKETİ] Ağırlık barkodu çözüldü:', weightLabel);
        try {
          const product = await salesService.getProductByScaleCode(weightLabel.scaleCode, branchId);

          if (!product) {
            pushScanAlert({
              term: weightLabel.scaleCode,
              title: 'Tartı Ürünü Bulunamadı',
              message:
                `Etiketteki '${weightLabel.scaleCode}' tartı koduna sahip ürün bulunamadı.\n\n` +
                `Ürün kartında tartı kodunun tanımlı olduğundan emin olun.`,
              // Hızlı stok kaydı tartı kodu alanı sunmuyor; yeni kart açmak bu
              // etiketi okutulabilir hâle getirmez, çözüm mevcut karta tartı kodu girmek
              canQuickAdd: false,
            });
            focusSearchBar();
            return;
          }

          const eklendi = await searchAndAddProductToCart(weightLabel.scaleCode, configuredWarehouseIds!, {
            preFetchedProduct: product,
            forcedQuantity: weightLabel.weightKg,
            // Ağırlık zaten etiketten geldi; tam 1.000 kg'da kg modalı açılmasın
            skipOptionModals: true,
          });

          console.log('⚖️ [TARTI ETİKETİ] Sepete ekleme sonucu:', eklendi);
          return;
        } catch (error: any) {
          console.error('❌ [TARTI ETİKETİ] Ürün getirilemedi:', error);
          showAlertModal('Tartı Ürünü Hatası', `Etiketteki ürün getirilemedi: ${error?.message || error}`);
          return;
        }
      }
    }

    // 3. Ürün ara ve sepete ekle (SQL'den direkt arama)
    const searchTermLower = term.toLowerCase();
    console.log('🚀 [5/6] SQL ARAMA BAŞLIYOR:', { 
      arananTerim: searchTermLower,
      depoIdleri: configuredWarehouseIds,
      companyId: companyId
    });
    
    const aramaBasarili = await searchAndAddProductToCart(searchTermLower, configuredWarehouseIds!);
    
    console.log('🏁 [6/6] ARAMA TAMAMLANDI:', { 
      basarili: aramaBasarili,
      bilgi: aramaBasarili ? 'Ürün sepete eklendi (state async güncelleniyor)' : 'Ürün eklenemedi'
    });
    console.log('═══════════════════════════════════════════════════════════');
  }, [isRestaurantMode, screenSettings, companyId, currentScreen, allWarehouses, showAlertModal, pushScanAlert, focusSearchBar, validateSearchPrerequisites, searchAndAddProductToCart, branchWarehouseId, readWeightBarcode, weightBarcodePrefixes, branchId]);

  /**
   * Autocomplete dropdown'dan seçilen ürünü işleyip sepete ekler.
   * searchAndAddProductToCart'tan farklı olarak yeniden API araması yapmaz;
   * zaten elimizde olan ham ürün verisini doğrulayıp sepete ekler.
   */
  const handleAutocompleteSelect = useCallback(async (rawProduct: any): Promise<void> => {
    const configuredWarehouseIds = screenSettings?.stockVisibility?.warehouses;
    if (!configuredWarehouseIds?.length) {
      showAlertModal('Depo Seçilmemiş', 'Bu ekran için Ayarlar bölümünden en az bir depo seçmeniz gerekiyor.');
      return;
    }

    // Depo bilgisini çöz
    const warehouseStocks = rawProduct.warehouseStocks || rawProduct.WarehouseStocks || [];
    let effectiveWarehouseId = rawProduct.warehouseId?.toString() || rawProduct.WarehouseId?.toString() || '';
    let effectiveTotalQuantity = rawProduct.totalQuantity || rawProduct.TotalQuantity || 0;

    if (!effectiveWarehouseId && warehouseStocks.length > 0) {
      const configuredStock = warehouseStocks.find((ws: any) =>
        configuredWarehouseIds.includes(ws.warehouseId?.toString() || ws.WarehouseId?.toString() || '')
      );
      const chosenStock = configuredStock || warehouseStocks[0];
      effectiveWarehouseId = chosenStock.warehouseId?.toString() || chosenStock.WarehouseId?.toString() || '';
      effectiveTotalQuantity = chosenStock.quantity || chosenStock.Quantity || 0;
    }

    const inventoryItem: InventoryItem = {
      id: rawProduct.id?.toString() || rawProduct.Id?.toString() || '',
      name: rawProduct.name || rawProduct.Name || '',
      salePrice: rawProduct.salePrice || rawProduct.SalePrice || 0,
      categoryId: rawProduct.categoryId?.toString() || rawProduct.CategoryId?.toString() || '',
      warehouseId: effectiveWarehouseId,
      quantity: effectiveTotalQuantity,
      unit: rawProduct.unit || rawProduct.Unit || 'adet',
      barcode: rawProduct.barcode || rawProduct.Barcode || '',
      imageUrl: rawProduct.thumbnailUrl || rawProduct.ThumbnailUrl || rawProduct.imageUrl || rawProduct.ImageUrl || null,
      vatRate: rawProduct.vatRate || rawProduct.VatRate || 0,
      vatStatus: rawProduct.vatStatus || rawProduct.VatStatus || 'excluded',
      canBeNegative: rawProduct.canBeNegative || rawProduct.CanBeNegative || false,
      departmentNo: rawProduct.departmentNo || rawProduct.DepartmentNo || null,
      stockCode: rawProduct.stockCode || rawProduct.StockCode || '',
    };

    // Şube bazlı fiyat
    const useBranchPricing = rawProduct.useBranchPricing ?? rawProduct.UseBranchPricing ?? false;
    if (useBranchPricing && branchId && companyId) {
      try {
        const { InventoryService } = await import('../../../accounting/inventory/services/inventoryService');
        const branchPricesDict = await InventoryService.getBulkBranchPrices(companyId, branchId, [inventoryItem.id]);
        const branchPrice = branchPricesDict[inventoryItem.id.toLowerCase()] || branchPricesDict[inventoryItem.id];
        if (branchPrice) {
          const branchSalePrice = branchPrice.salePrice || (branchPrice as any).SalePrice || 0;
          // ✅ YENİ: Paket modu veya ekran ayarı açıksa PackagePrice öncelik, yoksa SalePrice
          const branchPackagePrice = (branchPrice as any).packagePrice || (branchPrice as any).PackagePrice || 0;
          const effectivePrice = (shouldUsePackagePrice && branchPackagePrice > 0)
            ? branchPackagePrice
            : branchSalePrice;
          if (effectivePrice > 0) {
            inventoryItem.salePrice = effectivePrice;
            inventoryItem.vatStatus = 'included';
          }
        }
      } catch {
        // varsayılan fiyatla devam et
      }
    }

    // Fiyat kontrolü
    if (!inventoryItem.salePrice || inventoryItem.salePrice === 0) {
      showAlertModal('Fiyat Tanımsız', `'${inventoryItem.name}' ürününün satış fiyatı tanımlı değil.`);
      return;
    }

    // Stok kontrolü
    const stockQty = inventoryItem.quantity ?? 0;
    if (stockQty <= 0 && !inventoryItem.canBeNegative) {
      showAlertModal(
        'Stok Yok',
        `'${inventoryItem.name}' ürününün stoğu yetersiz!\n\nMevcut Stok: ${stockQty}`
      );
      return;
    }

    const quantityFromNumpad = parseInt(quantityInputValue);
    const quantityToAdd =
      isQuantityModeActive && !isNaN(quantityFromNumpad) && quantityFromNumpad > 0
        ? quantityFromNumpad
        : 1;

    const productForCart = {
      id: inventoryItem.id,
      name: inventoryItem.name,
      price: inventoryItem.salePrice,
      salePrice: inventoryItem.salePrice,
      unit: inventoryItem.unit,
      vatRate: inventoryItem.vatRate,
      vatStatus: inventoryItem.vatStatus,
      canBeNegative: inventoryItem.canBeNegative,
      departmentNo: inventoryItem.departmentNo,
      warehouseId: inventoryItem.warehouseId,
      barcode: inventoryItem.barcode,
      stockCode: inventoryItem.stockCode,
      quantity: inventoryItem.quantity,
    };

    if (isRestaurantMode) {
      addToCartWithRestaurant(productForCart, quantityToAdd);
    } else {
      if (useCashierSystem && !currentCashierId) {
        if (onOpenCashierLoginWithPendingProduct) {
          onOpenCashierLoginWithPendingProduct(productForCart, quantityToAdd);
        }
        if (isQuantityModeActive) {
          setIsQuantityModeActive(false);
          setQuantityInputValue('');
        }
        return;
      }
      addToCart(productForCart, quantityToAdd);
      focusSearchBar();
    }

    if (isQuantityModeActive) {
      setIsQuantityModeActive(false);
      setQuantityInputValue('');
    }
  }, [
    screenSettings, companyId, branchId, isRestaurantMode, allWarehouses,
    isQuantityModeActive, quantityInputValue, setIsQuantityModeActive, setQuantityInputValue,
    addToCart, addToCartWithRestaurant, showAlertModal, focusSearchBar,
    useCashierSystem, currentCashierId, onOpenCashierLoginWithPendingProduct,
    isPackageMode, usePackagePrices, shouldUsePackagePrice,
  ]);

  return {
    getWarehouseNames,
    validateSearchPrerequisites,
    searchAndAddProductToCart,
    handleSearchSubmit,
    handleAutocompleteSelect,
  };
};

export default useQuickSaleProductSearch;
